import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  type SimpleChanges,
  OnChanges
} from '@angular/core';
import {
  EnterpriseSearchService,
  FlightJourney,
  FlightSeatMap,
  FlightSeatMapSeats,
  UserService,
  HelperRoutines,
  FlightSeatMapSeatAttributes
} from '@sabstravtech/obtservices/angular';
import { UserFavorurite, ServiceProvider } from '@sabstravtech/obtservices/base';

import { SeatMapType } from '../../../../../core/enum/seat-map-type.enum';

import { FlightSeat } from './flight-set.interface';

@Component({
  selector: 'app-flight-seat-map',
  templateUrl: './flight-seat-map.component.html',
  styleUrls: ['./flight-seat-map.component.scss'],
  standalone: false
})
export class FlightSeatMapComponent implements OnInit, OnChanges {
  @Input() requestActive = true;
  @Input() flight: FlightJourney = null;
  @Input() flightSeats: FlightSeatMap = null;
  @Input() supplier = '';
  @Input() travellerId = '';
  @Input() selectedSeatWithId: { [x: string]: FlightSeatMapSeats[] } = {};
  @Output() seat: EventEmitter<{ selectedSeat: FlightSeatMapSeats; id: string }> =
    new EventEmitter();
  @Output() loyaltyRetrieved: EventEmitter<string> = new EventEmitter<string>();
  @Output() setSeatsReceived: EventEmitter<boolean> = new EventEmitter<boolean>();

  seatKeys: FlightSeat[][];
  seats: FlightSeat[];
  seatsReceived = false;
  localSelectedSeat: FlightSeat;
  loyaltyCode = '';
  aisleSeatLocation = [];
  freeSeatsOnly = false;
  isSabreRyanair = false;
  constructor(
    public searchService: EnterpriseSearchService,
    private userService: UserService,
    private helpers: HelperRoutines
  ) {}

  ngOnInit() {
    const flightUIConfig: any = this.userService.getUserFavoriteObject(
      UserFavorurite.FlightResultsConfiguration
    );

    // console.log(`+++ Flight UI Config: `, flightUIConfig);

    this.freeSeatsOnly =
      flightUIConfig?.GDS?.showFreeSeatsOnly &&
      this.helpers.checkFlightProviderType(<ServiceProvider>this.supplier).GDS; // ? - check that we are on a GDS flight as well?

    console.log(`+++ Free seats only enabled: ${this.freeSeatsOnly} +++`);
    console.log(this.supplier);
    this.isSabreRyanair =
      this.supplier === ServiceProvider.SabreFlight &&
      (this.flight.marketingCarrierName === 'Ryanair' ||
        this.flight.operatingCarrierName === 'Ryanair');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.flightSeats?.currentValue) {
      this.getFlightSeatMap();
    }
    if (changes.selectedSeatWithId) {
      const prevValue = changes.selectedSeatWithId.previousValue;
      const currValue = changes.selectedSeatWithId.currentValue;

      if (prevValue && currValue && prevValue !== currValue && this.flightSeats) {
        console.log('Seat ID has changed:', currValue);
        this.getFlightSeatMap();
      }
    }
  }

  getFlightSeatMap(): void {
    this.seats = this.flightSeats.seats;

    for (const seat of this.seats) {
      const seatCode = seat.attributes.map((r) => r.code);
      const isAisleSeat =
        seatCode.includes(SeatMapType.AisleSeat) && !seatCode.includes(SeatMapType.CenterSeat);
      if (isAisleSeat) {
        this.aisleSeatLocation.push(seat.column);
      }
      if (this.isSabreRyanair && !seat.price && seat.available && !this.localSelectedSeat?.row) {
        this.selectedSeat(seat as FlightSeatMapSeats);
      }
    }

    // this.seats.map((seat: FlightSeat) => (seat.selected = false));

    if (this.flight.marketingCarrierName?.includes('Jet2')) {
      /// remove this after -- For ENT-16356 jet2 have a issues booking with seat map, so we are not showing the seat map for jet2 flights. This is a temporary fix until TFX have a fix for it.
      // Temporary: Jet2 returns no seat rows, so initialise the empty map to
      // let the template render its no-results state instead of the spinner.
      this.seatKeys = Object.create(null); // remove this after
      this.setSeatsReceived.emit(false); // remove this after
    } else {
      this.getSeatKeys();
    }
  }

  getSeatKeys(): void {
    this.seatKeys = this.seats.reduce((prev, current) => {
      prev[current.row] = prev[current.row] || [];
      prev[current.row].push(current);
      return prev;
    }, Object.create(null));
    this.seatsReceived = !!Object.keys(this.seatKeys).length;
    this.setSeatsReceived.emit(this.seatsReceived);
    if (this.seatsReceived) {
      this.fillEmptySpaces();
    }
  }

  fillEmptySpaces(): void {
    // Fills in any blank spaces for UX reasons
    const fullSeatKey = Object.values(this.seatKeys).reduce((a, b) =>
      b.length > a.length ? b : a
    );
    Object.keys(this.seatKeys).forEach((key) => {
      const seatKey = this.seatKeys[key];
      seatKey.forEach((seat) => {
        const seatsWithCurrentColumn = fullSeatKey.filter(
          (alreadyAssignedSeat) => alreadyAssignedSeat.column === seat.column
        ); // Ensure every possible column is added to the full seat key
        if (!seatsWithCurrentColumn.length) {
          fullSeatKey.push(seat);
        }
      });
    });
    fullSeatKey.sort((a, b) => a.column.localeCompare(b.column)); // Ensure the full seat key columns are in alphabetical order
    Object.keys(this.seatKeys).forEach((key) => {
      // Go through each seatkey, and fill in any blank spaces
      const seatKey = this.seatKeys[key];
      if (seatKey.length < fullSeatKey.length) {
        for (let x = 0; x < fullSeatKey.length; x++) {
          if (!seatKey[x]) {
            // If this index of this seat doesn't exist on the seatkey, add a blank seat
            const blankSeat = {} as FlightSeat;
            blankSeat.column = fullSeatKey[x].column;
            blankSeat.row = seatKey[0].row;
            blankSeat.attributes = fullSeatKey[x].attributes;
            blankSeat.isFake = true;
            seatKey.push(blankSeat); // Blank seat will show as unavailable so won't affect functionality
          } else if (seatKey[x].column !== fullSeatKey[x].column) {
            // If the column is incorrect, add a blank seat with the correct column
            const attributes = fullSeatKey[x].attributes;
            const isAisleSeat = attributes?.filter((att) => att.code === SeatMapType.AisleSeat);
            if (isAisleSeat) {
              const isBeforeAisleSeat = x
                ? seatKey[x - 1].attributes?.find((att) => att.code === SeatMapType.AisleSeat)
                : false;
              const isBFAisleSeat = x
                ? fullSeatKey
                    .find((att) => att.column === seatKey[x - 1].column)
                    ?.attributes?.find((att) => att.code === SeatMapType.AisleSeat)
                : false;
              if (isBeforeAisleSeat && !isBFAisleSeat) {
                seatKey[x - 1].isRemoveSpaces = true;
              } else if (!isBeforeAisleSeat && isBFAisleSeat) {
                seatKey[x - 1].isAddSpaces = true;
              }

              const isAfterAisleSeat = seatKey[x].attributes?.find(
                (att) => att.code === SeatMapType.AisleSeat
              );
              if (fullSeatKey.length >= x) {
                const isAFAisleSeat = fullSeatKey
                  .find((att) => att.column === seatKey[x].column)
                  ?.attributes?.find((att) => att.code === SeatMapType.AisleSeat);
                if (isAfterAisleSeat && !isAFAisleSeat) {
                  seatKey[x].isRemoveSpaces = true;
                } else if (!isAfterAisleSeat && isAFAisleSeat) {
                  seatKey[x].isAddSpaces = true;
                }
              }
            }

            const blankSeat = {} as FlightSeat;
            blankSeat.column = fullSeatKey[x].column;
            blankSeat.row = seatKey[0].row;
            blankSeat.attributes = attributes;
            blankSeat.isFake = true;
            seatKey.splice(x, 0, blankSeat); // Blank seat will show as unavailable so won't affect functionality
          }
        }
      }
    });
  }

  selectedSeat(selectedSeat: FlightSeatMapSeats): void {
    // this is the only update we have
    const beforeUpdateSeat = this.localSelectedSeat;
    this.localSelectedSeat = selectedSeat;

    if (this.flightSeats.loyaltyCode) {
      selectedSeat.attributes.push({
        code: this.flightSeats.loyaltyCode,
        description: 'Loyalty: ' + this.flight.operatingCarrier
      });
    }

    this.seats.forEach((seat: FlightSeat) => {
      if (!seat.selected && seat.row === selectedSeat.row && seat.column === selectedSeat.column) {
        seat.selected = true;
        seat.userId = this.travellerId;
        this.seat.emit({ selectedSeat, id: this.flight.id });
        // we should make anything false that is handled by a different person/flight
      } else if (
        seat.selected &&
        seat.row === selectedSeat.row &&
        seat.column === selectedSeat.column
      ) {
        this.seat.emit({ selectedSeat: null, id: this.flight.id });
        delete seat.selected;
        this.localSelectedSeat = null;
      } else if (
        seat.selected ||
        (seat.row === beforeUpdateSeat?.row && seat.column === beforeUpdateSeat?.column)
      ) {
        const otherUserSeatsArray = this.selectedSeatWithId[this.flight.id].filter(
          (si) => si.userId !== this.travellerId
        );
        const isSeatForOtherUser = otherUserSeatsArray.some(
          (otherSeat) => otherSeat.row === seat.row && otherSeat.column === seat.column
        );
        if (!isSeatForOtherUser) {
          seat.available = true;
          delete seat.selected;
        }
      }
    });
    this.getSeatKeys();
  }

  clearSelectedSeat(): void {
    this.localSelectedSeat = null;
    this.seat.emit({ selectedSeat: null, id: this.flight.id });
    this.seats.forEach((seat: FlightSeat) => {
      seat.selected = false;
      this.getSeatKeys();
    });
  }

  isSelected(seat: FlightSeat): boolean {
    return (
      (seat.selected === true && seat.userId === this.travellerId) ||
      (seat.row === this.localSelectedSeat?.row &&
        seat.column === this.localSelectedSeat?.column) ||
      (this.selectedSeatWithId[this.flight.id]?.length && // Check if seat is selected by another user
        !!this.selectedSeatWithId[this.flight.id].filter(
          (selectedSeat) =>
            selectedSeat.column === seat.column &&
            selectedSeat.row === seat.row &&
            selectedSeat.userId !== this.travellerId
        ).length)
    );
  }

  isNotAvailable(seat: FlightSeat): boolean {
    return (
      (!seat.available &&
        !(
          seat.row === this.localSelectedSeat?.row && seat.column === this.localSelectedSeat?.column
        )) ||
      (seat.price && this.freeSeatsOnly) ||
      (this.selectedSeatWithId[this.flight.id]?.length && // Check if seat has already been selected by another user, if so disable the button
        !!this.selectedSeatWithId[this.flight.id].filter(
          (selectedSeat) =>
            selectedSeat.column === seat.column &&
            selectedSeat.row === seat.row &&
            selectedSeat.userId !== this.travellerId
        ).length) ||
      this.shouldLeaveSeatVacant(seat)
    );
  }

  shouldLeaveSeatVacant(seat: FlightSeat): boolean {
    return seat.attributes.some((attribute: FlightSeatMapSeatAttributes) =>
      attribute.description?.includes('SeatToBeLeftVacant')
    );
  }
}
